/**
 * UsersPanel — Manage platform users (Users tab inside /users/admin).
 * Each user owns a PIN + permission matrix that lets them unlock
 * specific sections (/consultations, /pharmacy, etc.) without needing
 * the shared admin credentials.
 *
 * Auth: the parent /users/admin page holds a `badge_token`. We exchange
 * that for a `pp_token` (see POST /api/pp/auth/exchange-badge-token) on
 * first mount and reuse it for all user CRUD.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plus, Search, Trash2, Pencil, Loader2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Section access — mirrors the tabs inside the MD Scheduler dashboard.
const TAB_PERMISSIONS = [
  { key: "tab_md",        label: "MD schedule" },
  { key: "tab_all",       label: "All appointments" },
  { key: "tab_pharmacy",  label: "Pharmacy" },
  { key: "tab_calendar",  label: "Calendar" },
  { key: "tab_inventory", label: "Inventory" },
  { key: "tab_rewards",   label: "Rewards" },
  { key: "tab_referrals", label: "Referrals" },
  { key: "tab_expenses",  label: "Expenses" },
  { key: "tab_stats",     label: "Stats" },
  { key: "tab_locker",    label: "Locker" },
  { key: "tab_marketing", label: "Marketing" },
  { key: "tab_bloodwork", label: "Bloodwork" },
  { key: "tab_survey",    label: "Survey" },
  { key: "invoicing",     label: "Invoicing" },
];

// Other gated areas outside the dashboard tabs.
const OTHER_PERMISSIONS = [
  { key: "consultations",   label: "MD Scheduler (app sign-in)" },
  { key: "pharmacy",        label: "Partner Pharmacies (app)" },
  { key: "users_admin",     label: "User/Admin" },
  { key: "badge",           label: "Badge" },
  { key: "outreach_edit",   label: "Outreach admin" },
  { key: "learn_edit",      label: "Learn library edit" },
  { key: "operations_edit", label: "Operations (expenses)" },
  { key: "inventory",       label: "Inventory tracker (app)" },
  { key: "inventory_admin", label: "Inventory tracker (admin)" },
  { key: "case_stages_admin", label: "Case management" },
];


const EMPTY_USER = {
  first_name: "",
  last_name: "",
  email: "",
  pin: "",
  permissions: { consultations: false, pharmacy: false, users_admin: false, badge: false, invoicing: false, outreach_edit: false, learn_edit: false, operations_edit: false, inventory: false, inventory_admin: false, case_stages_admin: false, tab_md: false, tab_all: false, tab_pharmacy: false, tab_calendar: false, tab_inventory: false, tab_rewards: false, tab_referrals: false, tab_expenses: false, tab_stats: false, tab_locker: false, tab_marketing: false, tab_bloodwork: false, tab_survey: false },
  badge_id: "",
  active: true,
  staff_name: "",   // scheduling staff scoping — e.g. "George Holness"
  staff_team: "",   // expenses team lock — team_1 / team_2 / team_3
  work_type_keys: [],   // extra work-hour options assigned to this user (Work/Admin/Driving are always included)
};

// Generate a random 4-digit PIN that isn't obviously guessable
const randomPin = () => String(Math.floor(1000 + Math.random() * 9000));

export default function UsersPanel({ badgeToken }) {
  const [ppToken, setPpToken] = useState(null);
  const [exchangeError, setExchangeError] = useState("");
  const [items, setItems] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [dialog, setDialog] = useState(null); // null | {mode:'new'|'edit', user}
  const [busyId, setBusyId] = useState(null);

  // Exchange badge admin token for a pp admin token
  useEffect(() => {
    if (!badgeToken) return;
    (async () => {
      try {
        const res = await fetch(`${API}/pp/auth/exchange-badge-token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ badge_token: badgeToken }),
        });
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          setExchangeError(b.detail || "Session exchange failed");
          return;
        }
        const d = await res.json();
        setPpToken(d.token);
      } catch (_) {
        setExchangeError("Network error during session exchange");
      }
    })();
  }, [badgeToken]);

  const load = useCallback(async () => {
    if (!ppToken) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/pp/users`, { headers: { "X-Auth-Token": ppToken } });
      if (!res.ok) throw new Error("load failed");
      const d = await res.json();
      setItems(Array.isArray(d) ? d : []);
    } catch (e) {
      toast.error("Couldn't load users");
    } finally {
      setLoading(false);
    }
  }, [ppToken]);

  useEffect(() => { load(); }, [load]);

  // Shared staff/team registry — drives the staff-name suggestions and the
  // expenses-team dropdown (new staff get their own team automatically).
  // Reloaded after every save so the next dialog sees the newest teams.
  const [staffRegistry, setStaffRegistry] = useState({ staff: [], teams: [] });
  const loadRegistry = useCallback(async () => {
    if (!ppToken) return;
    try {
      const res = await fetch(`${API}/consultations/staff-teams`, { headers: { "X-Auth-Token": ppToken } });
      if (!res.ok) throw new Error("load failed");
      const d = await res.json();
      setStaffRegistry({
        staff: Array.isArray(d?.staff) ? d.staff : [],
        teams: Array.isArray(d?.teams) ? d.teams : [],
      });
    } catch { /* registry suggestions are optional */ }
  }, [ppToken]);
  useEffect(() => { loadRegistry(); }, [loadRegistry]);

  // Load the shared work-hour option list for per-user assignment checkboxes.
  useEffect(() => {
    if (!ppToken) return;
    (async () => {
      try {
        const res = await fetch(`${API}/consultations/outreach-work-types`, {
          headers: { "X-Auth-Token": ppToken },
        });
        if (!res.ok) throw new Error("load failed");
        const d = await res.json();
        setWorkTypes(Array.isArray(d?.types) ? d.types : []);
      } catch {
        toast.error("Couldn't load work-type options");
      }
    })();
  }, [ppToken]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((u) =>
      `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(q)
    );
  }, [items, query]);

  const openNew = () => setDialog({
    mode: "new",
    user: { ...EMPTY_USER, pin: randomPin() },
  });

  const openEdit = async (row) => {
    // Fetch full record (with real PIN) since list masks it
    try {
      const res = await fetch(`${API}/pp/users/${row.id}`, {
        headers: { "X-Auth-Token": ppToken },
      });
      if (!res.ok) throw new Error("load failed");
      const full = await res.json();
      setDialog({ mode: "edit", user: {
        ...EMPTY_USER,
        ...full,
        permissions: { ...EMPTY_USER.permissions, ...(full.permissions || {}) },
      }});
    } catch (e) {
      toast.error("Couldn't open user");
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete user ${u.first_name} ${u.last_name}?`)) return;
    setBusyId(u.id);
    try {
      const res = await fetch(`${API}/pp/users/${u.id}`, {
        method: "DELETE",
        headers: { "X-Auth-Token": ppToken },
      });
      if (!res.ok) throw new Error("delete failed");
      toast.success("User deleted");
      await load();
    } catch (e) {
      toast.error("Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  if (exchangeError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        {exchangeError}
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by name or email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
            data-testid="users-search"
          />
        </div>
        <Button
          onClick={openNew}
          className="bg-brand-teal hover:bg-brand-teal-dark text-white flex-shrink-0"
          data-testid="users-new-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          New user
        </Button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[520px]">
          <thead className="bg-slate-100 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Name</th>
              <th className="text-left px-4 py-3 font-semibold">Email</th>
              <th className="text-left px-4 py-3 font-semibold">PIN</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-right px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="users-list">
            {loading && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">
                No users yet — click <b>New user</b> to add one.
              </td></tr>
            )}
            {filtered.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`users-row-${u.id}`}>
                <td className="px-4 py-3 font-medium text-slate-900 whitespace-nowrap">
                  {u.first_name} {u.last_name}
                </td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{u.email || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-700 whitespace-nowrap">{u.pin}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {u.active ? (
                    <span className="text-xs text-emerald-700">active</span>
                  ) : (
                    <span className="text-xs text-slate-400">inactive</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <div className="inline-flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`users-edit-${u.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => removeUser(u)}
                      disabled={busyId === u.id}
                      data-testid={`users-delete-${u.id}`}
                    >
                      {busyId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 text-red-600" />}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dialog && (
        <UserDialog
          dialog={dialog}
          token={ppToken}
          workTypes={workTypes}
          onWorkTypesChange={setWorkTypes}
          staffRegistry={staffRegistry}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); load(); loadRegistry(); }}
        />
      )}
    </div>
  );
}

function UserDialog({ dialog, token, workTypes = [], onWorkTypesChange, staffRegistry = { staff: [], teams: [] }, onClose, onSaved }) {
  const [form, setForm] = useState(dialog.user);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  // Track badge auto-fill state so we can (a) show a status message and
  // (b) safely overwrite fields we auto-filled without clobbering the
  // user's own edits.
  const [badgeStatus, setBadgeStatus] = useState("idle"); // idle | loading | ok | not_found | error
  const [autoFilled, setAutoFilled] = useState({ first_name: "", last_name: "" });
  const [newTypeLabel, setNewTypeLabel] = useState("");
  const [typesBusy, setTypesBusy] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const togglePerm = (k) => setForm((f) => ({
    ...f, permissions: { ...f.permissions, [k]: !f.permissions[k] },
  }));

  const BUILT_IN_WORK_TYPES = ["outreach", "admin", "driving", "repair"];
  const customWorkTypes = workTypes.filter((t) => !BUILT_IN_WORK_TYPES.includes(t.key));
  // Only options explicitly ticked here are visible to this employee — there
  // is no automatic link to the master list.
  const selectedCustomWorkTypes = (form.work_type_keys || []).filter((k) => !BUILT_IN_WORK_TYPES.includes(k));
  const toggleWorkType = (key) => {
    const selected = new Set(selectedCustomWorkTypes);
    if (selected.has(key)) selected.delete(key);
    else selected.add(key);
    const next = customWorkTypes.map((t) => t.key).filter((k) => selected.has(k));
    setField("work_type_keys", next);
  };

  // Master-list management lives ONLY here in the admin section. A newly added
  // option is ticked on for this employee automatically; nobody else sees it
  // until it is ticked on their own profile.
  const saveMasterTypes = async (types) => {
    const res = await fetch(`${API}/consultations/outreach-work-types`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "X-Auth-Token": token },
      body: JSON.stringify({ types }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.detail || "save failed");
    return Array.isArray(d?.types) ? d.types : types;
  };
  const addWorkType = async () => {
    const label = newTypeLabel.trim();
    if (!label || typesBusy) return;
    const key = label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 24);
    if (!key || workTypes.some((t) => t.key === key)) { setNewTypeLabel(""); return; }
    setTypesBusy(true);
    try {
      const types = await saveMasterTypes([...workTypes, { key, label }]);
      onWorkTypesChange?.(types);
      setField("work_type_keys", [...selectedCustomWorkTypes, key]);
      setNewTypeLabel("");
    } catch (e) {
      toast.error(e?.message || "Couldn't add the option");
    } finally {
      setTypesBusy(false);
    }
  };
  const removeWorkType = async (key) => {
    if (typesBusy) return;
    setTypesBusy(true);
    try {
      const types = await saveMasterTypes(workTypes.filter((t) => t.key !== key));
      onWorkTypesChange?.(types);
      setField("work_type_keys", selectedCustomWorkTypes.filter((k) => k !== key));
    } catch (e) {
      toast.error(e?.message || "Couldn't remove the option");
    } finally {
      setTypesBusy(false);
    }
  };

  // Staff scoping defaults. On a NEW user the scheduling staff name follows
  // the typed first + last name until the admin edits that field by hand.
  const [staffTouched, setStaffTouched] = useState(dialog.mode !== "new");
  useEffect(() => {
    if (staffTouched) return;
    const full = `${form.first_name || ""} ${form.last_name || ""}`.trim();
    setForm((f) => ({ ...f, staff_name: full }));
  }, [form.first_name, form.last_name, staffTouched]);
  // The team that matches the staff name: existing staff → their own team;
  // brand-new staff → a preview of the team that will be created on save
  // (same "next number + initials" rule the backend uses).
  const knownStaff = staffRegistry.staff.find(
    (s) => s.full.trim().toLowerCase() === (form.staff_name || "").trim().toLowerCase()
  );
  const pendingTeam = (() => {
    const name = (form.staff_name || "").trim();
    if (!name || knownStaff) return null;
    const nums = staffRegistry.teams
      .map((t) => parseInt((t.key || "").split("_")[1], 10))
      .filter((n) => !isNaN(n));
    const n = (nums.length ? Math.max(...nums) : 0) + 1;
    const words = name.split(/\s+/);
    // Single first initial — consistent with the original teams (G, M, R).
    const initials = words[0][0].toUpperCase();
    return { key: `team_${n}`, label: `Team ${n} (${initials})` };
  })();
  // Default the team dropdown for NEW users so it shows exactly what the
  // save will do. Tracks the last auto-applied value so it follows along as
  // the name is typed, but never overrides a team the admin picked by hand.
  const autoTeamRef = useRef("");
  useEffect(() => {
    if (dialog.mode !== "new") return;
    const desired = knownStaff?.team || pendingTeam?.key || "";
    const cur = form.staff_team || "";
    if (!desired) return;
    if (cur === desired) { autoTeamRef.current = desired; return; }
    if (cur && cur !== autoTeamRef.current) return; // admin's own pick — keep
    autoTeamRef.current = desired;
    setForm((f) => ({ ...f, staff_team: desired }));
  }, [knownStaff?.team, pendingTeam?.key, form.staff_team, dialog.mode]);

  // Auto-fill first/last name from the badge registry when the user types
  // a badge ID. Debounced by ~450ms. Won't overwrite a name the user has
  // typed manually (only overwrites empty fields, or the previous
  // auto-filled value).
  useEffect(() => {
    const id = (form.badge_id || "").trim().toUpperCase();
    if (id.length < 3) {
      setBadgeStatus("idle");
      return;
    }
    let cancelled = false;
    setBadgeStatus("loading");
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/badge/verify/${encodeURIComponent(id)}`);
        const data = await res.json();
        if (cancelled) return;
        if (data?.ok && data?.name) {
          const [first, ...rest] = data.name.trim().split(/\s+/);
          const last = rest.join(" ");
          setForm((f) => ({
            ...f,
            first_name: !f.first_name || f.first_name === autoFilled.first_name ? first : f.first_name,
            last_name:  !f.last_name  || f.last_name  === autoFilled.last_name  ? last  : f.last_name,
          }));
          setAutoFilled({ first_name: first, last_name: last });
          setBadgeStatus("ok");
        } else {
          setBadgeStatus("not_found");
        }
      } catch {
        if (!cancelled) setBadgeStatus("error");
      }
    }, 450);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [form.badge_id]);

  const save = async (e) => {
    e?.preventDefault?.();
    setErr("");
    const payload = {
      first_name: form.first_name.trim(),
      last_name:  form.last_name.trim(),
      email:      (form.email || "").trim(),
      pin:        (form.pin || "").trim(),
      permissions: form.permissions,
      badge_id:   form.badge_id || null,
      active:     !!form.active,
      staff_name: (form.staff_name || "").trim() || null,
      staff_team: form.staff_team || null,
      work_type_keys: form.work_type_keys || [],
    };
    if (!payload.first_name || !payload.last_name) {
      setErr("First and last name are required."); return;
    }
    if (payload.pin.length < 4) {
      setErr("PIN must be at least 4 characters."); return;
    }
    setSaving(true);
    try {
      const url = dialog.mode === "edit"
        ? `${API}/pp/users/${form.id}`
        : `${API}/pp/users`;
      const method = dialog.mode === "edit" ? "PATCH" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", "X-Auth-Token": token },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        setErr(b.detail || `HTTP ${res.status}`);
        return;
      }
      toast.success(dialog.mode === "edit" ? "User updated" : "User created");
      onSaved();
    } catch (_) {
      setErr("Network error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl max-h-[92vh] overflow-y-auto" data-testid="user-dialog">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-brand-navy">
            {dialog.mode === "edit" ? "Edit user" : "New user"}
          </h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">First name</Label>
              <Input value={form.first_name} onChange={(e) => setField("first_name", e.target.value)} data-testid="user-fname" autoFocus />
            </div>
            <div>
              <Label className="text-xs">Last name</Label>
              <Input value={form.last_name} onChange={(e) => setField("last_name", e.target.value)} data-testid="user-lname" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Email (optional)</Label>
            <Input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} data-testid="user-email" />
          </div>
          <div>
            <Label className="text-xs">PIN (4–8 characters)</Label>
            <div className="flex gap-2">
              <Input value={form.pin} onChange={(e) => setField("pin", e.target.value.replace(/\s+/g, ""))} data-testid="user-pin" className="font-mono" />
              <Button type="button" variant="outline" onClick={() => setField("pin", randomPin())} data-testid="user-pin-generate">
                Generate
              </Button>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">This is what the user types on the login screen.</p>
          </div>

          <div>
            <Label className="text-xs">Staff scoping (scheduling) — optional</Label>
            <div className="mt-1 space-y-2 rounded-lg border border-slate-200 p-3">
              <div>
                <Label className="text-[11px] text-slate-500">Scheduling staff name</Label>
                <Input value={form.staff_name || ""} onChange={(e) => { setStaffTouched(true); setField("staff_name", e.target.value); }}
                  placeholder="Defaults to the user's name" data-testid="user-staff-name" list="staff-name-options" />
                <datalist id="staff-name-options">
                  {staffRegistry.staff.map((s) => (
                    <option key={s.full} value={s.full} />
                  ))}
                </datalist>
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Expenses team</Label>
                <select value={form.staff_team || ""} onChange={(e) => setField("staff_team", e.target.value)}
                  data-testid="user-staff-team"
                  className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm">
                  <option value="">— not locked —</option>
                  {staffRegistry.teams.map((t) => (
                    <option key={t.key} value={t.key}>{t.label}</option>
                  ))}
                  {pendingTeam && !staffRegistry.teams.some((t) => t.key === pendingTeam.key) && (
                    <option value={pendingTeam.key}>{pendingTeam.label} — new on save</option>
                  )}
                </select>
              </div>
              <p className="text-[11px] text-slate-500">
                When a staff name is set, this PIN sees only their own calendar, bookings, outreach,
                expenses team and stats — and Referrals stays hidden. Leave empty for office/admin accounts.
                For new users the staff name defaults to their own name, and the team defaults to their
                own new team (created on save) — a new staff name also gets its own Calendar pill,
                Rewards + Expenses team and Stats card.
              </p>
            </div>
          </div>

          <div>
            <Label className="text-xs">Work-hour options</Label>
            <div className="mt-1 space-y-2 rounded-lg border border-slate-200 p-3" data-testid="user-work-types">
              <p className="text-[11px] text-slate-500">
                Outreach, Admin, Driving and Repair are always included for everyone. Tick any additional
                options this employee should see — unticked options stay hidden from them.
              </p>
              {workTypes.length === 0 ? (
                <p className="text-[11px] text-slate-400">Loading work-type options…</p>
              ) : customWorkTypes.length === 0 ? (
                <p className="text-[11px] text-slate-400">No additional options yet — add one below.</p>
              ) : (
                <div className="space-y-1.5">
                  {customWorkTypes.map((t) => (
                    <div key={t.key} className="flex items-center gap-2">
                      <label className="flex flex-1 items-center gap-2 text-sm cursor-pointer" data-testid={`user-work-type-${t.key}`}>
                        <input
                          type="checkbox"
                          checked={selectedCustomWorkTypes.includes(t.key)}
                          onChange={() => toggleWorkType(t.key)}
                          className="h-4 w-4 rounded border-slate-300 text-brand-teal focus:ring-brand-teal"
                        />
                        <span>{t.label}</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => removeWorkType(t.key)}
                        disabled={typesBusy}
                        aria-label={`Remove ${t.label}`}
                        title="Remove option for everyone"
                        className="text-slate-400 hover:text-red-600 disabled:opacity-40"
                        data-testid={`user-work-type-remove-${t.key}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <Input
                  value={newTypeLabel}
                  onChange={(e) => setNewTypeLabel(e.target.value)}
                  placeholder="New option name"
                  data-testid="user-work-type-new"
                  className="h-8 text-sm"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addWorkType}
                  disabled={typesBusy || !newTypeLabel.trim()}
                  data-testid="user-work-type-add"
                  className="h-8"
                >
                  Add
                </Button>
              </div>
              <p className="text-[11px] text-slate-500">
                Options can only be added or removed here in the admin section. A new option is
                ticked on for this employee automatically — tick it on other profiles as needed.
              </p>
            </div>
          </div>

          <div>
            <Label className="text-xs">Section access</Label>
            <div className="mt-1 space-y-1.5 rounded-lg border border-slate-200 p-3">
              <p className="text-[11px] text-slate-500">
                These match the tabs inside the MD Scheduler. Checked tabs are the only ones this
                user sees after signing in — leave every tab unchecked to show all tabs.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {TAB_PERMISSIONS.map((p) => (
                  <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`user-perm-${p.key}`}>
                    <input
                      type="checkbox"
                      checked={!!form.permissions[p.key]}
                      onChange={() => togglePerm(p.key)}
                      className="h-4 w-4 rounded border-slate-300 text-brand-teal focus:ring-brand-teal"
                    />
                    <span className="font-medium">{p.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs">Other areas</Label>
            <div className="mt-1 space-y-1.5 rounded-lg border border-slate-200 p-3">
              {OTHER_PERMISSIONS.map((p) => (
                <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`user-perm-${p.key}`}>
                  <input
                    type="checkbox"
                    checked={!!form.permissions[p.key]}
                    onChange={() => togglePerm(p.key)}
                    className="h-4 w-4 rounded border-slate-300 text-brand-teal focus:ring-brand-teal"
                  />
                  <span className="font-medium">{p.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">Linked badge ID (optional)</Label>
            <Input
              value={form.badge_id || ""}
              onChange={(e) => setField("badge_id", e.target.value.toUpperCase())}
              placeholder="HC-000123"
              className="font-mono"
              data-testid="user-badge-id"
            />
            {badgeStatus === "loading" && (
              <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1" data-testid="user-badge-status-loading">
                <Loader2 className="w-3 h-3 animate-spin" /> Looking up badge…
              </p>
            )}
            {badgeStatus === "ok" && (
              <p className="text-[11px] text-emerald-700 mt-1" data-testid="user-badge-status-ok">
                ✓ Auto-filled name from badge <b>{form.badge_id}</b>. Edit if needed.
              </p>
            )}
            {badgeStatus === "not_found" && (
              <p className="text-[11px] text-amber-700 mt-1" data-testid="user-badge-status-notfound">
                No badge matches this ID. You can still save this user — the badge can be created later.
              </p>
            )}
            {badgeStatus === "idle" && (
              <p className="text-[11px] text-slate-500 mt-1">
                Leave blank if this user doesn&rsquo;t have a physical badge yet. Type a badge ID to auto-fill the name.
              </p>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!form.active} onChange={(e) => setField("active", e.target.checked)} data-testid="user-active" />
            <span>Active — can log in</span>
          </label>

          {err && <p className="text-xs text-red-600" data-testid="user-error">{err}</p>}

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 bg-brand-navy hover:bg-brand-navy-dark text-white"
              data-testid="user-save"
            >
              {saving ? "Saving…" : "Save user"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
